import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SortDir } from '../lib/list-state'

/**
 * The state a list screen keeps: sort, search, filters, pagination.
 *
 * It holds state and nothing else — it never sees rows and never fetches. A
 * server-paginated screen turns this into query params; a client-side screen
 * feeds it to `sortRows`. That is why there is no "mode" flag: the two never
 * share a code path.
 *
 * Extracted from UsersPage, which had all of this inline, and from the copy of
 * the sort half that Business and Infrastructure each grew separately.
 */
export interface UseListStateOptions<K extends string, F extends Record<string, string>> {
  /** Column sorted on first render. */
  sortKey: K
  sortDir?: SortDir
  /**
   * Direction a column starts in the first time you sort by it. Defaults to
   * descending, which is right for counts and money; override for the columns
   * that read better ascending (names, and margins when you want the losses
   * first).
   */
  defaultDirFor?: (key: K) => SortDir
  pageSize?: number
  /** Initial filter values. `filtersActive` compares against these. */
  filters?: F
  debounceMs?: number
}

export interface ListState<K extends string, F extends Record<string, string>> {
  sortKey: K
  sortDir: SortDir
  /** Same column flips direction; a new column starts at its default. */
  toggleSort: (key: K) => void
  /** What the user is typing. */
  search: string
  setSearch: (value: string) => void
  /** What a query should use — `search`, settled. */
  debouncedSearch: string
  page: number
  setPage: (page: number) => void
  pageSize: number
  setPageSize: (size: number) => void
  filters: F
  setFilter: <N extends keyof F>(name: N, value: F[N]) => void
  resetFilters: () => void
  /** True when search or any filter is off its initial value. */
  filtersActive: boolean
}

const EMPTY_FILTERS = {} as Record<string, string>

export function useListState<K extends string, F extends Record<string, string> = Record<string, string>>(
  options: UseListStateOptions<K, F>,
): ListState<K, F> {
  const {
    sortKey: initialSortKey,
    sortDir: initialSortDir = 'desc',
    defaultDirFor,
    pageSize: initialPageSize = 20,
    filters: initialFilters = EMPTY_FILTERS as F,
    debounceMs = 300,
  } = options

  const [sortKey, setSortKey] = useState<K>(initialSortKey)
  const [sortDir, setSortDir] = useState<SortDir>(initialSortDir)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [filters, setFilters] = useState<F>(initialFilters)

  // What the user types is not what the query uses: without this, every
  // keystroke is a request.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), debounceMs)
    return () => clearTimeout(timer)
  }, [search, debounceMs])

  const filtersFingerprint = JSON.stringify(filters)

  // Narrowing the result set while sitting on page 7 lands you on an empty
  // page. Any change to what is being asked for goes back to the first page.
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, sortKey, sortDir, pageSize, filtersFingerprint])

  const toggleSort = useCallback(
    (key: K) => {
      setSortKey((current) => {
        if (current === key) {
          setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
        } else {
          setSortDir(defaultDirFor ? defaultDirFor(key) : 'desc')
        }
        return key
      })
    },
    [defaultDirFor],
  )

  const setFilter = useCallback(<N extends keyof F>(name: N, value: F[N]) => {
    setFilters((f) => ({ ...f, [name]: value }))
  }, [])

  const resetFilters = useCallback(() => {
    setSearch('')
    setFilters(initialFilters)
    // The debounce would otherwise keep the old term for another tick.
    setDebouncedSearch('')
  }, [initialFilters])

  const initialFingerprint = useMemo(() => JSON.stringify(initialFilters), [initialFilters])
  const filtersActive = debouncedSearch !== '' || filtersFingerprint !== initialFingerprint

  return {
    sortKey,
    sortDir,
    toggleSort,
    search,
    setSearch,
    debouncedSearch,
    page,
    setPage,
    pageSize,
    setPageSize,
    filters,
    setFilter,
    resetFilters,
    filtersActive,
  }
}
